// src/pages/PrivacyPolicy.jsx
import { useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

// ── Local Storage Items ──────────────────────────────────────────────────
const STORAGE_ITEMS = [
    {
        id: 'gmc_token',
        type: 'Authentication',
        purpose: 'Secure Login Session',
        duration: 'Until browser closes or logout',
        description: 'Keeps you securely logged into your personal dashboard while you work. It vanishes automatically the moment you sign out or close your web browser, and contains no personal text or message data.',
        essential: true,
    },
    {
        id: 'gmc_user',
        type: 'Preference',
        purpose: 'Cached Account Profile',
        duration: 'Until logout or profile update',
        description: 'Stores your name and plan tier locally so the dashboard loads instantly without a round-trip to the server. This contains only basic account metadata and is never used for tracking.',
        essential: false,
    },
    {
        id: 'gmc_theme',
        type: 'Preference',
        purpose: 'Visual Theme Setting',
        duration: 'Indefinite (until changed)',
        description: 'Remembers whether you prefer viewing your workspace in Dark Mode or Light Mode. This stays entirely inside your own browser window and is never transmitted back to our cloud servers.',
        essential: false,
    },
    {
        id: 'gmc_onboarding',
        type: 'Preference',
        purpose: 'Onboarding Status',
        duration: 'Indefinite (until reset)',
        description: 'Prevents the introductory tour from repeating every time you visit the platform. This contains no personal information and is purely for your convenience.',
        essential: false,
    },
]

const TYPE_COLORS = {
    Authentication: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    Security: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Preference: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
}

// ── Storage item row ─────────────────────────────────────────────────────
function StorageCard({ item, expanded, onToggle }) {
    const badgeStyle = TYPE_COLORS[item.type]

    return (
        <div
            className="bg-white/[0.01] hover:bg-white/[0.02] rounded-xl overflow-hidden transition-all duration-300 cursor-pointer border backdrop-blur-sm select-none"
            style={{
                borderColor: expanded ? 'rgba(212, 168, 67, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                boxShadow: expanded ? '0 4px 24px rgba(212,168,67,0.02)' : 'none',
            }}
            onClick={onToggle}
        >
            {/* Header row */}
            <div className="px-6 py-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                    <span className="font-sans font-medium text-sm text-white tracking-wide shrink-0">
                        {item.id}
                    </span>
                    <span className={`text-xs font-sans font-medium uppercase tracking-wider px-2.5 py-0.5 rounded-full flex-shrink-0 border ${badgeStyle}`}>
                        {item.type}
                    </span>
                    <span className="text-xs font-body font-light text-slate-400 truncate hidden sm:block">
                        {item.purpose}
                    </span>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs font-body font-light text-slate-500 hidden md:block">
                        {item.duration}
                    </span>
                    {item.essential && (
                        <div className="flex items-center gap-1.5 text-xs font-sans font-medium tracking-wider uppercase text-slate-500">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Required
                        </div>
                    )}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', stroke: expanded ? '#ffb000' : '#64748b', transition: 'all 250ms ease' }}>
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            {/* Expanded detail panel */}
            {expanded && (
                <div className="px-6 pb-6 border-t border-white/[0.03] bg-white/[0.005]">
                    <div className="pt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                        <div>
                            <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-500 mb-1">
                                Storage Key
                            </p>
                            <p className="text-sm font-sans font-medium text-slate-300">
                                {item.id}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-500 mb-1">
                                Lifespan
                            </p>
                            <p className="text-sm font-body font-light text-slate-400">{item.duration}</p>
                        </div>
                        <div>
                            <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-500 mb-1">
                                Classification
                            </p>
                            <div className="pt-0.5">
                                <span className={`text-xs font-sans font-medium uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${badgeStyle}`}>
                                    {item.type}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-500 mb-2">
                            What it does
                        </p>
                        <p className="text-base font-body text-slate-400 font-light leading-[1.85]">
                            {item.description}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Main Page Layout ────────────────────────────────────────────────────
export default function PrivacyPolicy() {
    const [expanded, setExpanded] = useState(null)
    const toggle = (id) => setExpanded(e => e === id ? null : id)

    return (
        <div className="min-h-screen bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden flex flex-col selection:bg-brand-amber/30 selection:text-white">

            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-gradient-to-b from-indigo-500/5 to-transparent blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-[-10%] w-[500px] h-[500px] bg-brand-amber/[0.02] blur-[130px] pointer-events-none rounded-full" />

            <Navbar />

            <div className="flex-1 w-full px-6 py-20 md:py-28 relative z-10 flex items-center justify-center">
                <div className="w-full max-w-3xl">

                    {/* -- Header Block ---------------------------------------- */}
                    <div className="mb-14">
                        <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-emerald-400/[0.06] border border-emerald-400/25 rounded-full shadow-inner backdrop-blur-md mb-6">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" style={{ animationDuration: '2.5s' }} />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                            </span>
                            <span className="text-sm font-sans font-semibold tracking-widest uppercase text-emerald-400">
                                Audit Status: Compliant
                            </span>
                        </div>

                        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6 leading-tight text-center sm:text-left">
                            Privacy Policy
                        </h1>

                        <div className="border-l-2 pl-5 mb-8 border-brand-amber/40">
                            <p className="text-lg font-body text-slate-300 font-light leading-[1.8] italic">
                                "At GMC, we view user tracking data as a clear liability, not an asset. Our stance is absolute: we store only the baseline functional identifiers required to secure your active work account and personalise your experience — and nothing more."
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-sans font-light text-slate-400 justify-center sm:justify-start">
                            <span>Last updated: June 2026</span>
                            <span className="w-px h-4 bg-white/[0.10]" />
                            <span>Storage items: 4</span>
                            <span className="w-px h-4 bg-white/[0.10]" />
                            <span>Third-party tracking: 0</span>
                            <span className="w-px h-4 bg-white/[0.10]" />
                            <span className="inline-flex items-center gap-2 text-emerald-400 font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399]" />
                                No cookies used
                            </span>
                        </div>
                    </div>

                    {/* -- What We Never Use ------------------------------------ */}
                    <section className="mb-14">
                        <p className="block text-slate-400 text-xs font-sans font-semibold tracking-widest uppercase mb-4 ml-1">
                            What we never include
                        </p>

                        <div className="rounded-xl overflow-hidden bg-white/[0.01] border border-white/[0.05] backdrop-blur-sm shadow-xl">
                            <div className="flex items-start gap-4 px-6 py-5 border-b border-white/[0.04]">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center mt-0.5">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                        stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-sans font-medium text-white mb-1.5 tracking-wide">No cookies are used</p>
                                    <p className="text-sm font-body font-light text-slate-400 leading-relaxed">
                                        Guardian Message Check sets no cookies at all. Nothing server-side sets a cookie, the frontend sets none, and there are no third-party scripts (no analytics, tag managers, or pixels). The only external resource the site loads is Google Fonts.
                                    </p>
                                </div>
                            </div>

                            {[
                                ['No marketing pixels or ad trackers', 'We use zero advertising trackers from third parties. Your professional browsing habits are never monitored or packaged.'],
                                ['No cross-site user tracking', 'We do not build diagnostic fingerprints, trade tracking profiles, or share behaviour markers with secondary advertising broker channels.'],
                                ['No behavioural recordings', 'We deliberately avoid click-mapping tools, screen recorders, and invasive marketing metrics. Your workspace workflow is yours alone.'],
                            ].map(([label, desc], i, arr) => (
                                <div key={label}
                                    className={`flex items-start gap-4 px-6 py-5 ${i < arr.length - 1 ? 'border-b border-white/[0.04]' : ''}`}>
                                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center mt-0.5">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                            stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-sans font-medium text-white mb-1.5 tracking-wide">{label}</p>
                                        <p className="text-sm font-body font-light text-slate-400 leading-relaxed">{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* -- Storage Inventory ----------------------------------- */}
                    <section className="mb-14">
                        <div className="flex items-center justify-between mb-4 px-1">
                            <p className="text-slate-400 text-xs font-sans font-semibold tracking-widest uppercase">
                                Local Storage Items
                            </p>
                            <span className="text-xs font-sans font-light text-slate-500">
                                Select an item to view full details
                            </span>
                        </div>

                        <div className="grid grid-cols-3 gap-4 px-6 pb-2.5 hidden sm:grid border-b border-white/[0.03] mb-2">
                            {['Storage Key', 'Type', 'Lifespan'].map(h => (
                                <p key={h} className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-500">
                                    {h}
                                </p>
                            ))}
                        </div>

                        <div className="space-y-3">
                            {STORAGE_ITEMS.map(item => (
                                <StorageCard
                                    key={item.id}
                                    item={item}
                                    expanded={expanded === item.id}
                                    onToggle={() => toggle(item.id)}
                                />
                            ))}
                        </div>
                    </section>

                    {/* -- Control & Settings Section --------------------------- */}
                    <section className="mb-14">
                        <p className="block text-slate-400 text-xs font-sans font-semibold tracking-widest uppercase mb-4 ml-1">
                            Your Personal Controls
                        </p>
                        <div className="bg-white/[0.01] border border-white/[0.05] rounded-xl p-6 backdrop-blur-sm shadow-xl">
                            <p className="text-base font-body font-light text-slate-400 leading-[1.85] mb-5">
                                Authentication tokens and account data cannot be turned off manually because they are essential to secure your dashboard session. However, the theme and onboarding flags can be cleared via your browser's site data settings.
                            </p>
                            <p className="text-base font-body font-light text-slate-400 leading-[1.85] mb-7">
                                If you wish to wipe out all stored data instantly, open your web browser preferences, browse to your Site Data configuration panel, and select remove all data for{' '}
                                <span className="font-sans font-medium text-xs bg-white/[0.04] px-2 py-0.5 rounded text-amber-400 border border-white/[0.05]">
                                    guardianmessagecheck.com
                                </span>.
                                Wiping these keys will safely sign you out of your account session.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {[
                                    { browser: 'Google Chrome', steps: 'Settings › Privacy › Site Data' },
                                    { browser: 'Mozilla Firefox', steps: 'Preferences › Privacy & Security' },
                                    { browser: 'Apple Safari', steps: 'Settings › Privacy › Website Data' },
                                ].map(b => (
                                    <div key={b.browser}
                                        className="rounded-lg px-4 py-3.5 border border-white/[0.03] bg-white/[0.005]">
                                        <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-500 mb-1">
                                            {b.browser}
                                        </p>
                                        <p className="text-sm font-body font-light text-slate-400 leading-relaxed">
                                            {b.steps}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* -- Commitment Footer ---------------------------------- */}
                    <div className="rounded-xl border border-white/[0.04] relative overflow-hidden bg-gradient-to-r from-white/[0.01] to-white/[0.02]">
                        <div className="px-8 py-8 relative overflow-hidden backdrop-blur-md">
                            <div className="absolute inset-0 pointer-events-none"
                                style={{ background: 'radial-gradient(ellipse 60% 80% at 100% 50%, rgba(255,176,0,0.02) 0%, transparent 60%)' }} />
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-4">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                        stroke="#ffb000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <p className="text-xs font-sans font-semibold tracking-widest uppercase text-brand-amber">
                                        Our Ongoing Commitment
                                    </p>
                                </div>
                                <p className="text-base font-body text-slate-400 font-light leading-[1.85] mb-5">
                                    These storage rules cover <span className="text-white font-medium">guardianmessagecheck.com</span> and its core analytical routes. If structural updates alter our storage framework, this breakdown will reflect those changes immediately.
                                </p>
                                <p className="text-sm font-body font-light text-slate-400">
                                    Have specific feedback about our privacy guidelines? Message our support team directly at{' '}
                                    <a href="mailto:admin@guardianmessagecheck.com" className="mail-link">
                                        admin@guardianmessagecheck.com
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    )
}